import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { tournamentStatusService } from "@/frontend/services";
import type {
  FlatPaginationRequestDTO,
  TournamentStatusResponseDTO,
} from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: FlatPaginationRequestDTO = {
  page: 1,
  pageSize: 7,
};

export function TournamentStatusesListPage() {
  const navigate = useNavigate();
  const { data, loading, pagination, loadData } =
    usePagination<TournamentStatusResponseDTO>({
      fetchFn: tournamentStatusService.getPaginate,
      initialParams: initialParams,
      initialPagination: initialPagination,
    });

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } =
    useDelete({
      deleteFn: tournamentStatusService.delete,
      loadData,
      successMessage: "Estado eliminado exitosamente",
      errorMessage: "Error al eliminar estado",
      showConfirm: true,
      confirmTitle: "¿Eliminar estado de torneo?",
      confirmDescription:
        "Esta acción eliminará el estado permanentemente. ¿Estás seguro?",
    });

  const columns: ColumnsType<TournamentStatusResponseDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (description: string | null) => description || "-",
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      render: (color: string | null) => {
        if (!color) return "-";
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded border border-gray-300"
              style={{ backgroundColor: color }}
            />
            <span>{color}</span>
          </div>
        );
      },
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
    },
    {
      title: "Activo",
      dataIndex: "isActive",
      key: "isActive",
      render: (v: boolean) => (v ? "Sí" : "No"),
    },
    {
      title: "Orden",
      dataIndex: "sortOrder",
      key: "sortOrder",
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: any, record: TournamentStatusResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaEdit />}
            onClick={() => navigate(`/tournament-statuses/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger icon={<FaTrash />}>
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Gestión de Estados de Torneo
          </h2>
          <p className="text-gray-600 mt-1">
            Administra los estados disponibles para los torneos
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/tournament-statuses/create")}
        >
          Crear Estado
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
